import {useMemo} from 'react'
import type {Theme, BaseColumnChartProps, ColumnChartProps, ColumnChartStyles} from '../types'
import {MarkColor} from '../types'
import styles from '../../ChartCard.module.css'
import type {SymbolClass} from './shared'
import {
  getDataWithDefaults,
  getTransformedPlotOptions,
  getXAxisOptionWithDefaults,
  getYAxisOptionsWithDefaults,
  isDev,
  getSymbolStyles,
} from './shared'
import Chart from '../Chart'
import {useLocalStorage} from '@github-ui/use-safe-storage/local-storage'
import {CHART_CARD_CUSTOMIZATION_SETTINGS} from '../CustomizationDialog'
import {sanitize} from '@github-ui/sanitize'

const MAX_NUMBER_OF_SERIES_ITEMS = 5

const CONVERTED_BORDER_STYLES = {
  Solid: 'solid',
  ShortDash: 'dashed',
}

export function getLegendLabel(
  series: Highcharts.SeriesOptionsType[],
  name: string,
  index: number,
  colorsTransformed: Array<string | Highcharts.GradientColorObject>,
): string {
  const currentSeries = (series[index] as Partial<Highcharts.SeriesColumnOptions>) ?? {}
  const symbolShape = 'square'
  const color = colorsTransformed[index] ?? `var(--display-green-scale-${index})`
  const borderColor = typeof currentSeries?.borderColor === 'string' ? currentSeries.borderColor : undefined

  const dashStyle = currentSeries?.dashStyle as keyof typeof CONVERTED_BORDER_STYLES | undefined
  const borderShape = dashStyle ? CONVERTED_BORDER_STYLES[dashStyle] : undefined
  const symbolStyle = getSymbolStyles({color, borderColor, borderShape, symbol: symbolShape})
  const symbolClass = `ChartCardSymbol-${symbolShape}` as SymbolClass
  // eslint-disable-next-line github/unescaped-html-literal
  const symbol = `<span class="${styles.ChartCardSymbol} ${
    styles[symbolClass as keyof typeof styles]
  }" style="${symbolStyle}"></span>`
  // eslint-disable-next-line github/unescaped-html-literal
  const label = `<span>${sanitize(name, {ALLOWED_TAGS: [], RETURN_TRUSTED_TYPE: false})}</span>`
  return `${symbol} ${label}`
}

export const getTheme = (
  color: Theme,
  increasedContrast: boolean = false,
): Array<string | Highcharts.GradientColorObject> => {
  if (increasedContrast === false) {
    return [
      `var(--display-${color}-scale-8)`,
      `var(--display-${color}-scale-4)`,
      `var(--display-${color}-scale-2)`,
      `var(--display-${color}-scale-1)`,
      `var(--display-${color}-scale-0)`,
    ]
  }
  return [
    `var(--display-${color}-scale-9)`,
    `var(--display-${color}-scale-5)`,
    {
      linearGradient: {
        x1: 0,
        x2: 0,
        y1: 1,
        y2: 0,
      },
      stops: [
        [0.15, `var(--display-${color}-scale-1)`],
        [0.45, `var(--display-${color}-scale-2)`],
        [1, `var(--display-${color}-scale-4)`],
      ],
    },
    {
      linearGradient: {
        x1: 0,
        x2: 0,
        y1: 1,
        y2: 0,
      },
      stops: [
        [0, `var(--display-${color}-scale-0)`],
        [1, `var(--display-${color}-scale-1)`],
      ],
    },
    `var(--display-${color}-scale-0)`,
  ]
}

export const getThemeBySeriesCount = (seriesCount: number, theme: Theme, increasedContrast: boolean = false) => {
  return getTheme(theme, increasedContrast).slice(0, seriesCount).reverse()
}

export const ColumnDefaultColor = {
  pine: MarkColor.pine,
  teal: MarkColor.teal,
  cyan: MarkColor.cyan,
  blue: MarkColor.blue,
  indigo: MarkColor.indigo,
  purple: MarkColor.purple,
  orange: MarkColor.orange,
}

export const BaseColumnChart = <T extends 'bar' | 'column'>({
  type,
  options,
  series: data,
  stacking,
  labels: initialLabelsValue,
  colors,
  theme: userTheme,
  overrideOptionsNotRecommended,
  ...rest
}: BaseColumnChartProps<T> & ColumnChartStyles) => {
  const {xAxis, yAxis, plot} = options ?? {}
  const [increasedContrast] = useLocalStorage(CHART_CARD_CUSTOMIZATION_SETTINGS.chartCardIncreasedContrast, false)
  const [userLabelsValue] = useLocalStorage(CHART_CARD_CUSTOMIZATION_SETTINGS.chartCardColumnDataLabels, undefined)
  const labels = userLabelsValue ?? initialLabelsValue
  const xyAxisOptionsTransformed = useMemo(() => getXAxisOptionWithDefaults({xAxis, gridLineWidth: 0}), [xAxis])
  const theme = useMemo(
    () => getThemeBySeriesCount(data?.length ?? 0, userTheme ?? 'green', increasedContrast),
    [userTheme, data, increasedContrast],
  )

  const isTheme = useMemo(() => (data?.length ?? 0) > 1, [data])
  const yAxisOptionsTransformed = useMemo(
    () => getYAxisOptionsWithDefaults({yAxis, gridLineDashStyle: 'Dash'}),
    [yAxis],
  )
  const seriesWithBorder = useMemo(() => {
    return data?.map((series, idx) => {
      if (stacking && series.colorByPoint) {
        // eslint-disable-next-line no-console
        console.warn('Stacking is enabled, colorByPoint will be set to false for all series.')
      }

      const is3rd = JSON.stringify(theme[idx]) === JSON.stringify(getTheme(userTheme ?? 'green', increasedContrast)[3])
      const is4th = JSON.stringify(theme[idx]) === JSON.stringify(getTheme(userTheme ?? 'green', increasedContrast)[4])

      return {
        ...series,
        colorByPoint: stacking ? false : series.colorByPoint,
        dashStyle:
          isTheme && increasedContrast === true ? (is3rd ? 'ShortDash' : is4th ? 'Solid' : undefined) : 'Solid',
        borderColor: isTheme
          ? is3rd && increasedContrast === true
            ? `var(--display-${userTheme ?? 'green'}-scale-5)`
            : is4th && increasedContrast === true
              ? `var(--display-${userTheme ?? 'green'}-scale-5)`
              : undefined
          : undefined,
      }
    })
  }, [data, stacking, isTheme, theme, userTheme, increasedContrast])

  const series = useMemo(
    () =>
      getDataWithDefaults({
        series: seriesWithBorder as Highcharts.SeriesOptionsType[],
        type,
        hasDashStyle: false,
      }),
    [seriesWithBorder, type],
  )

  const plotOptionsTransformed = useMemo(
    () => getTransformedPlotOptions({plotOptions: plot, labels, stacking, noBorderRadius: true}),
    [labels, stacking, plot],
  )

  if (series.length > MAX_NUMBER_OF_SERIES_ITEMS && isDev()) {
    // eslint-disable-next-line no-console
    console.error('Ideally, a column chart should contain no more than 5 segments or columns.')
  }

  const colorsTransformed = useMemo(
    () =>
      isTheme
        ? theme
        : colors
          ? colors.map((color: keyof typeof MarkColor) => `var(${MarkColor[color]})`)
          : Object.keys(ColumnDefaultColor).map(
              key => `var(${ColumnDefaultColor[key as keyof typeof ColumnDefaultColor]})`,
            ),
    [colors, isTheme, theme],
  )

  return (
    <Chart
      type={type}
      xAxisOptions={xyAxisOptionsTransformed}
      yAxisOptions={yAxisOptionsTransformed}
      series={series as Highcharts.SeriesOptionsType[]}
      plotOptions={plotOptionsTransformed}
      colors={colorsTransformed}
      overrideOptionsNotRecommended={{
        ...overrideOptionsNotRecommended,
        legend: {
          useHTML: true,
          symbolHeight: 0,
          symbolWidth: 0,
          labelFormatter() {
            return getLegendLabel(series as Highcharts.SeriesOptionsType[], this.name, this.index, colorsTransformed)
          },
          ...overrideOptionsNotRecommended?.legend,
        },
      }}
      {...rest}
    />
  )
}

const ColumnChart = (props: ColumnChartProps) => {
  return <BaseColumnChart {...props} type="column" />
}

export default ColumnChart
